import { useState } from "react";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  ArrowLeft,
  ClipboardList,
  ScanLine,
  Truck,
  Store,
  Package,
  FileText,
  HelpCircle,
  CheckCircle2,
  AlertTriangle,
  ArrowRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function WorkflowGuidePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-4">
      <Navigation />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Arbetsflöden</h1>
            <p className="text-muted-foreground text-sm">
              Guide: Plocklista vs Leverans
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <HelpCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Översikt</span>
            </TabsTrigger>
            <TabsTrigger value="plocklista" className="gap-2">
              <ClipboardList className="h-4 w-4" />
              <span className="hidden sm:inline">Plocklista</span>
            </TabsTrigger>
            <TabsTrigger value="leverans" className="gap-2">
              <ScanLine className="h-4 w-4" />
              <span className="hidden sm:inline">Leverans</span>
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Vilket flöde ska jag använda?</CardTitle>
                <CardDescription>
                  Välj rätt arbetsflöde beroende på hur varorna ska levereras
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Decision tree */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-center font-medium mb-4">Ska varorna skickas med transportör?</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="border-blue-500/50 hover:border-blue-500 transition-colors cursor-pointer" onClick={() => setActiveTab("plocklista")}>
                      <CardContent className="p-4 text-center">
                        <div className="flex justify-center mb-2">
                          <div className="p-3 bg-blue-500/10 rounded-full">
                            <Truck className="h-8 w-8 text-blue-500" />
                          </div>
                        </div>
                        <p className="font-bold text-blue-500">JA - Bring/DSV</p>
                        <p className="text-sm text-muted-foreground mt-1">Använd Plocklista</p>
                        <Badge className="mt-2 bg-blue-500">CSV-import</Badge>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-green-500/50 hover:border-green-500 transition-colors cursor-pointer" onClick={() => setActiveTab("leverans")}>
                      <CardContent className="p-4 text-center">
                        <div className="flex justify-center mb-2">
                          <div className="p-3 bg-green-500/10 rounded-full">
                            <Store className="h-8 w-8 text-green-500" />
                          </div>
                        </div>
                        <p className="font-bold text-green-500">NEJ - Direkt</p>
                        <p className="text-sm text-muted-foreground mt-1">Använd Leverans</p>
                        <Badge className="mt-2 bg-green-500">Skanna EAN</Badge>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Quick comparison */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <ClipboardList className="h-5 w-5 text-blue-500" />
                      <h3 className="font-bold">Plocklista</h3>
                    </div>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Många ordrar samtidigt
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Optimerad plockrutt
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Bring & DSV-transporter
                      </li>
                    </ul>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <ScanLine className="h-5 w-5 text-green-500" />
                      <h3 className="font-bold">Leverans</h3>
                    </div>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Enskilda produkter
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Direkt utlämning
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Butik & rundleveranser
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FAQ */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  Vanliga frågor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="q1">
                    <AccordionTrigger>Varför minskas inte lagret?</AccordionTrigger>
                    <AccordionContent>
                      Lagret minskas direkt när du markerar en produkt som plockad (Plocklista) eller bekräftar utskanning (Leverans). 
                      Kontrollera att du faktiskt klickat "Plockad" och inte bara tittat på produkten.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="q2">
                    <AccordionTrigger>Kan jag använda plocklista för butikskunder?</AccordionTrigger>
                    <AccordionContent>
                      Tekniskt ja, men det rekommenderas inte. Plocklistan är optimerad för batch-plock av transportordrar. 
                      För enskilda butikskunder, använd Leverans/Scan-out för snabbare hantering.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="q3">
                    <AccordionTrigger>Måste jag ange ordernummer?</AccordionTrigger>
                    <AccordionContent>
                      <p className="mb-2">Ja, ordernummer krävs alltid för spårbarhet:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li><strong>Plocklista:</strong> Kommer från CSV-filen</li>
                        <li><strong>Leverans - Butik:</strong> Obligatoriskt</li>
                        <li><strong>Leverans - Runda:</strong> Obligatoriskt</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="q4">
                    <AccordionTrigger>Vad händer om jag väljer fel leveranstyp?</AccordionTrigger>
                    <AccordionContent>
                      Statistiken påverkas, men lagersaldot blir korrekt. Kontakta admin för att korrigera i historiken om det behövs.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PLOCKLISTA TAB */}
          <TabsContent value="plocklista" className="space-y-6">
            <Card className="border-blue-500/30">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-full">
                    <ClipboardList className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <CardTitle>Plocklista</CardTitle>
                    <CardDescription>För Bring/DSV-transporter och batch-plock</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* When to use */}
                <div>
                  <h3 className="font-bold mb-3">När används det?</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-3 bg-blue-500/5 rounded-lg flex items-center gap-2">
                      <Truck className="h-5 w-5 text-blue-500" />
                      <span className="text-sm">Transport (Bring/DSV)</span>
                    </div>
                    <div className="p-3 bg-blue-500/5 rounded-lg flex items-center gap-2">
                      <Package className="h-5 w-5 text-blue-500" />
                      <span className="text-sm">Flera ordrar</span>
                    </div>
                    <div className="p-3 bg-blue-500/5 rounded-lg flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-500" />
                      <span className="text-sm">CSV-import</span>
                    </div>
                  </div>
                </div>

                {/* Workflow */}
                <div>
                  <h3 className="font-bold mb-3">Arbetsflöde</h3>
                  <div className="space-y-3">
                    {[
                      { step: 1, title: "Importera CSV", desc: "Ladda upp orderfil från ordersystemet" },
                      { step: 2, title: "Systemet sorterar", desc: "Produkter sorteras efter lagerplats (optimal rutt)" },
                      { step: 3, title: "Plocka steg-för-steg", desc: "Markera varje produkt som plockad" },
                      { step: 4, title: "Ange plockplats", desc: "Bekräfta var produkten togs från" },
                      { step: 5, title: "Avsluta", desc: "Alla varor klara för transport" },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                          {item.step}
                        </div>
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What happens */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-bold mb-3">Vad händer i systemet?</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Lagersaldo minskas vid plock
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Historik loggas med typ "Plocklista"
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Statistik uppdateras för Bring/DSV
                    </li>
                  </ul>
                </div>

                <Button className="w-full" onClick={() => navigate("/picking")}>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Gå till Plocklista
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LEVERANS TAB */}
          <TabsContent value="leverans" className="space-y-6">
            <Card className="border-green-500/30">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-full">
                    <ScanLine className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <CardTitle>Leverans / Scan-out</CardTitle>
                    <CardDescription>För butik och lokala rundor</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* When to use */}
                <div>
                  <h3 className="font-bold mb-3">När används det?</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-3 bg-green-500/5 rounded-lg flex items-center gap-2">
                      <Store className="h-5 w-5 text-green-500" />
                      <span className="text-sm">Kund hämtar i butik</span>
                    </div>
                    <div className="p-3 bg-green-500/5 rounded-lg flex items-center gap-2">
                      <Truck className="h-5 w-5 text-green-500" />
                      <span className="text-sm">Lokal runda</span>
                    </div>
                    <div className="p-3 bg-green-500/5 rounded-lg flex items-center gap-2">
                      <Package className="h-5 w-5 text-green-500" />
                      <span className="text-sm">Enskilda produkter</span>
                    </div>
                  </div>
                </div>

                {/* Workflow */}
                <div>
                  <h3 className="font-bold mb-3">Arbetsflöde</h3>
                  <div className="space-y-3">
                    {[
                      { step: 1, title: "Skanna EAN", desc: "Eller sök manuellt på artikelnummer" },
                      { step: 2, title: "Välj leveranstyp", desc: "Butik eller Runda" },
                      { step: 3, title: "Ange referens", desc: "Ordernummer (obligatoriskt för runda)" },
                      { step: 4, title: "Bekräfta", desc: "Lager minskas direkt" },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm">
                          {item.step}
                        </div>
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Protection */}
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Skydd mot misstag
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Saldo 0 - blockeras med varning
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Allt bokat - dialog för att avboka först
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Ordernummer krävs för rundleveranser
                    </li>
                  </ul>
                </div>

                <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => navigate("/scan")}>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Gå till Skanna
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
